import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { type ApiError, toApiError } from './api-client';

/** What a screen can render: exactly one of loading, data or a translated failure. */
type ApiQueryState<T> =
  | {
      readonly status: 'pending';
      readonly data: undefined;
      readonly error: undefined;
      readonly refreshError: undefined;
      readonly receivedAt: undefined;
    }
  | {
      readonly status: 'success';
      readonly data: T;
      readonly error: undefined;
      /**
       * Failure of the last refresh, while `data` (the last good answer) stays on screen (M7 R7):
       * the screen shows its Offline state (no response, `ApiError.network`) or the problem's reason text.
       * Cleared by the next success.
       */
      readonly refreshError: ApiError | undefined;
      /**
       * When `data` arrived, on the device clock (`Date.now()`). Freshness is judged on it, not on
       * the server's `generatedAt`, so a device clock ahead of or behind the server never shows
       * old data as fresh (M7 R9; proposal, to be validated, sub-question of Q60).
       */
      readonly receivedAt: number;
    }
  | {
      /** The query never got data: there is nothing to keep, the screen shows the failure. */
      readonly status: 'error';
      readonly data: undefined;
      readonly error: ApiError;
      readonly refreshError: undefined;
      readonly receivedAt: undefined;
    };

/** {@link ApiQueryState} plus the refresh state and the action behind the Retry button. */
export type ApiQueryResult<T> = ApiQueryState<T> & {
  /**
   * A load is in flight while data or an error is already on screen (M7 R5, AC6): the screen
   * keeps what it shows and adds a discreet activity mark, never a spinner in place of the data.
   */
  readonly refreshing: boolean;
  /**
   * Loads again. The last data or error stays on screen until the new answer arrives, so a
   * refresh never flashes the loading state. A load still in flight is aborted first.
   */
  readonly refetch: () => void;
};

/** Options of {@link useApiQuery}. */
export interface ApiQueryOptions {
  /**
   * Refreshes every `intervalMs` while `active` (M7 R1–R3). Pass the app visibility as `active`
   * (`useAppVisible()`): no request leaves while the app is hidden, not even the first one, and
   * becoming visible again loads at once. Omitted: the query loads once.
   */
  readonly poll?: { readonly intervalMs: number; readonly active: boolean } | undefined;
}

/** {@link ApiQueryState} and which load attempt it answers, to tell when a load is in flight. */
interface Settled<T> {
  readonly query: ApiQueryState<T>;
  readonly answered: number;
}

/** Attempt number of a polled query mounted while the app is hidden: no load has started yet. */
const NOT_STARTED = -1;

const PENDING = {
  query: {
    status: 'pending',
    data: undefined,
    error: undefined,
    refreshError: undefined,
    receivedAt: undefined,
  },
  answered: NOT_STARTED,
} as const;

/**
 * One API read for one screen (master §5.1 "Server state"), in plain React: loads on mount,
 * aborts on unmount, never updates state after unmount, and reloads on {@link ApiQueryResult.refetch}.
 * There is no cache shared between screens and no automatic retry: the screen offers Retry, and a
 * polled query that already has data simply tries again at the next tick (M7 R7).
 *
 * A load is aborted only by a newer load (`refetch`, a new `load`) or by unmount; never by the
 * poll timer.
 *
 * With `options.poll` (M7, technical spec §4 "refresh every 5 s while the screen is visible"):
 * - the next load starts `intervalMs` after the previous one **started**, so the cadence stays at
 *   0, 5, 10 s… for fast answers (M7 AC1); a slow answer is never aborted by the timer: the next
 *   load waits for it to settle, so two polls never overlap and a slow network still gets answers;
 * - while `active` is false, no load starts (R2), including the first one when the screen mounts
 *   hidden; when it turns true again, one load starts at once and the interval restarts from it
 *   (R3, AC3), unless a load is still in flight: the cadence then continues from that one;
 * - a failed refresh keeps the last data and sets `refreshError` (R7, AC8);
 * - polling runs only once a load has succeeded: a first load that fails shows its error and
 *   waits for Retry (M1 §4 "Load failed"), it is never retried in the background.
 *
 * `load` must keep its identity between renders (wrap it in `useCallback`): a new `load` is a new
 * query and loads again, as a new attempt (the previous load is aborted, `refreshing` is true while
 * data is on screen). The new attempt is adjusted during render, so a `load` that changes on every
 * render now throws React's "Too many re-renders" instead of merely reloading: callers must
 * memoise it.
 */
export function useApiQuery<T>(
  load: (signal: AbortSignal) => Promise<T>,
  options: ApiQueryOptions = {},
): ApiQueryResult<T> {
  const intervalMs = options.poll?.intervalMs;
  const pollActive = options.poll?.active ?? false;
  const [settled, setSettled] = useState<Settled<T>>(PENDING);
  // Bumped by refetch and by the poll timer: a new value re-runs the load effect, which aborts
  // the previous load. A polled query mounted hidden starts at NOT_STARTED: nothing loads (R2).
  const [attempt, setAttempt] = useState(() =>
    intervalMs !== undefined && !pollActive ? NOT_STARTED : 0,
  );
  // When the current attempt started (Date.now(), mocked by fake timers in tests).
  const startedAt = useRef(0);
  // A new `load` is a new query: it counts as a new attempt, so `refreshing` and the poll timer
  // see it in flight. Adjusted during render (React "storing information from previous renders"),
  // so the load effect runs once with both changes, not once per change (two requests). A query
  // that has not started (hidden mount, R2) stays not started.
  const [loadSeen, setLoadSeen] = useState(() => load);
  if (loadSeen !== load) {
    setLoadSeen(() => load);
    setAttempt((n) => (n === NOT_STARTED ? n : n + 1));
  }

  useEffect(() => {
    if (attempt === NOT_STARTED) return undefined;
    const controller = new AbortController();
    startedAt.current = Date.now();
    load(controller.signal).then(
      (data) => {
        if (controller.signal.aborted) return;
        setSettled({
          query: {
            status: 'success',
            data,
            error: undefined,
            refreshError: undefined,
            receivedAt: Date.now(),
          },
          answered: attempt,
        });
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        const apiError = toApiError(error);
        setSettled(({ query }) => ({
          // R7: data already on screen stays; only the refresh failed.
          query:
            query.status === 'success'
              ? { ...query, refreshError: apiError }
              : {
                  status: 'error',
                  data: undefined,
                  error: apiError,
                  refreshError: undefined,
                  receivedAt: undefined,
                },
          answered: attempt,
        }));
      },
    );
    return () => {
      controller.abort();
    };
  }, [load, attempt]);

  const refetch = useCallback(() => {
    setAttempt((n) => n + 1);
  }, []);

  const inFlight = settled.answered !== attempt;
  const { query } = settled;
  usePollTimer({
    intervalMs,
    active: pollActive,
    // Poll once data is on screen, or to start the first load deferred while hidden.
    canPoll: query.status === 'success' || attempt === NOT_STARTED,
    inFlight,
    attempt,
    startedAt,
    refetch,
  });

  return { ...query, refreshing: inFlight && query.status !== 'pending', refetch };
}

/** Inputs of {@link usePollTimer}. */
interface PollTimerInput {
  readonly intervalMs: number | undefined;
  readonly active: boolean;
  readonly canPoll: boolean;
  readonly inFlight: boolean;
  readonly attempt: number;
  readonly startedAt: RefObject<number>;
  readonly refetch: () => void;
}

/**
 * Plans the next load of a polled query (M7 R1–R3): one timer at a time, armed only while the
 * app is visible, no load is in flight and {@link PollTimerInput.canPoll}. Internal to
 * {@link useApiQuery}.
 */
function usePollTimer({
  intervalMs,
  active,
  canPoll,
  inFlight,
  attempt,
  startedAt,
  refetch,
}: PollTimerInput): void {
  // Whether the app was visible at the previous run, to catch the instant it is visible again.
  const wasActive = useRef(active);
  // The attempt on screen when the app became visible again with no load in flight: until a
  // newer attempt starts, the next load leaves at once (R3).
  const resumeFrom = useRef<number | null>(null);

  useEffect(() => {
    if (intervalMs === undefined) return undefined;
    if (!active) {
      wasActive.current = false;
      return undefined;
    }
    if (!wasActive.current) {
      wasActive.current = true;
      // A load still in flight already brings new data: waiting for it avoids a double request.
      if (!inFlight) resumeFrom.current = attempt;
    }
    // Never overlap: the next poll is planned once the current load has settled.
    if (inFlight || !canPoll) return undefined;
    // Bounded by intervalMs on both sides: a device clock set back during a load would otherwise
    // make the elapsed time negative and push the next poll hours away.
    const delay =
      attempt === NOT_STARTED || resumeFrom.current === attempt
        ? 0
        : Math.min(intervalMs, Math.max(0, intervalMs - (Date.now() - startedAt.current)));
    const timer = setTimeout(refetch, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [intervalMs, active, canPoll, inFlight, attempt, startedAt, refetch]);
}
